// src/js/core/elevenLabsTTS.js
import { supabaseClient } from "./supabaseClient.js";
import { getAccentPreference, setAccentPreference } from "./preferencesManager.js";
import { updateVoiceSelect, updateVoiceSelectVisibility, getVoiceId, getDefaultAccent, getLocale } from "./voiceConfig.js";

const TTS_USAGE_INFO_KEY = "tts_usage_info";

export const initTTS = () => {
  const audioCache = new Map();
  let currentAudio = null;

  // Initialize accent on load
  const initialLanguage = document.getElementById("language-select").value;
  if (!getAccentPreference(initialLanguage)) {
    const defaultAccent = getDefaultAccent(initialLanguage);
    setAccentPreference(initialLanguage, defaultAccent);
  }
  updateVoiceSelect(initialLanguage);

  function clearCache() {
    for (const { audioUrl } of audioCache.values()) {
      URL.revokeObjectURL(audioUrl);
    }
    audioCache.clear();
  }

  // Add clearCache to window for access from generateDialogueUI
  window.clearTTSCache = clearCache;

  // Language change just updates accent if needed
  document.getElementById("language-select").addEventListener("change", (e) => {
    const newLanguage = e.target.value;
    if (!getAccentPreference(newLanguage)) {
      const defaultAccent = getDefaultAccent(newLanguage);
      setAccentPreference(newLanguage, defaultAccent);
    }
    updateVoiceSelect(newLanguage);
  });

  // Function to update all unused audio buttons with current usage info
  function updateTTSButtonsText() {
    const usageInfoStr = localStorage.getItem(TTS_USAGE_INFO_KEY);
    let usageCount = 0;
    let ttsLimit = 2; // Default limit
    let isAuthenticated = false;

    if (usageInfoStr) {
      const usageInfo = JSON.parse(usageInfoStr);
      ttsLimit = usageInfo.limit || ttsLimit;
      isAuthenticated = usageInfo.is_authenticated || false;

      // Check if the stored data is from the current day
      const now = new Date();
      const lastUpdated = new Date(usageInfo.last_updated);

      const isSameDay =
        now.getUTCFullYear() === lastUpdated.getUTCFullYear() &&
        now.getUTCMonth() === lastUpdated.getUTCMonth() &&
        now.getUTCDate() === lastUpdated.getUTCDate();

      if (isSameDay) {
        usageCount = usageInfo.daily_count;
      } else {
        localStorage.removeItem(TTS_USAGE_INFO_KEY);
      }
    }

    const remainingUses = Math.max(0, ttsLimit - usageCount);
    let buttonText;
    if (remainingUses === 0) {
      buttonText = isAuthenticated ? "0 left today" : "Sign in free for more audio";
    } else {
      buttonText = isAuthenticated ? `${remainingUses} left today` : "Play audio";
    }
    const noUsesRemaining = remainingUses === 0;

    // Only update buttons that haven't been played yet
    document.querySelectorAll(".audio-btn").forEach((btn) => {
      if (!btn.classList.contains("speaking") && !btn.classList.contains("audio-played")) {
        btn.textContent = buttonText;
        btn.classList.toggle("audio-btn-disabled", noUsesRemaining);
        btn.disabled = noUsesRemaining;
      }
    });
  }

  // Handle audio button clicks
  document.addEventListener("click", async (e) => {
    if (!e.target.matches(".audio-btn")) return;

    const button = e.target;
    const paragraphContainer = button.closest(".paragraph-container");
    const gender = paragraphContainer.classList.contains("speaker1") ? "male" : "female";

    const originalButtonText = button.textContent;
    button.innerHTML = '<div class="spin"></div>';
    button.disabled = true;

    const paragraphText = paragraphContainer.dataset.ttsText;
    const selectedLanguage = document.getElementById("language-select").value;
    const selectedAccent = getAccentPreference(selectedLanguage);
    const selectedLevel = document.getElementById("level-select").value;

    if (currentAudio) {
      currentAudio.pause();
      currentAudio = null;
      document.querySelectorAll(".audio-btn").forEach((btn) => {
        btn.classList.remove("speaking");
      });
    }

    button.classList.add("speaking");

    const voiceId = getVoiceId(selectedLanguage, selectedAccent, gender);
    const locale = getLocale(selectedLanguage, selectedAccent);
    if (!voiceId && !locale) {
      console.error(`No voice config found for: ${selectedLanguage}/${selectedAccent}/${gender}`);
      button.innerHTML = originalButtonText;
      button.disabled = false;
      button.classList.remove("speaking");
      return;
    }

    const cacheKey = `${voiceId}:${locale}:${selectedAccent}:${paragraphText}`;

    try {
      let audioUrl;
      let audio;

      const cachedAudio = audioCache.get(cacheKey);

      if (cachedAudio) {
        audioUrl = cachedAudio.audioUrl;
        audio = new Audio(audioUrl);
        button.innerHTML = originalButtonText;
        button.disabled = false;
      } else {
        // Get auth session
        const {
          data: { session },
        } = await supabaseClient.auth.getSession();

        // Call Netlify function
        const response = await fetch("/.netlify/functions/tts", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            text: paragraphText,
            voiceId: voiceId,
            level: selectedLevel,
            locale: locale,
            gender: gender,
          }),
        });

        const responseData = await response.json();

        // Store usage info in localStorage
        if (responseData.usage_info) {
          localStorage.setItem(
            TTS_USAGE_INFO_KEY,
            JSON.stringify({
              daily_count: responseData.usage_info.daily_count,
              limit_reached: responseData.usage_info.limit_reached,
              limit: responseData.usage_info.limit,
              is_authenticated: responseData.usage_info.is_authenticated,
              is_premium: responseData.usage_info.is_premium,
              user_type: responseData.usage_info.user_type,
              last_updated: new Date().toISOString(),
            })
          );

          updateTTSButtonsText();
        }

        if (response.status === 429) {
          throw new Error(`TTS API error: ${response.status}`);
        }

        if (!response.ok) {
          throw new Error(`TTS API error: ${response.status}`);
        }

        button.innerHTML = "Play again";
        button.disabled = false;
        button.classList.add("audio-played");

        // Convert base64 to blob
        const binaryData = atob(responseData.audioData);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const mimeType = responseData.audioMimeType || "audio/mpeg";
        const audioBlob = new Blob([bytes.buffer], { type: mimeType });

        audioUrl = URL.createObjectURL(audioBlob);
        audioCache.set(cacheKey, { audioUrl });

        audio = new Audio(audioUrl);
      }

      currentAudio = audio;

      audio.onerror = (error) => {
        console.error("Audio playback error:", error);
        button.classList.remove("speaking");
        button.innerHTML = originalButtonText;
        button.disabled = false;
        if (audioCache.has(cacheKey)) {
          URL.revokeObjectURL(audioCache.get(cacheKey).audioUrl);
          audioCache.delete(cacheKey);
        }
      };

      await audio.play();
    } catch (error) {
      console.error("TTS error:", error);
      button.classList.remove("speaking");
      button.innerHTML = originalButtonText;
      button.disabled = false;
    }
  });
};
